import { useState, useEffect, useCallback } from 'react';
import { getUserIncomeFinancials } from '../../../services/financials.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	Cell,
	TooltipProps,
} from 'recharts';
import { Warning } from '@phosphor-icons/react';
import Loading from '../../../components/atoms/Loading';

// Define interfaces for data structures
interface MonthlyRegimeData {
	regime: string;
	amount: number;
}

interface MonthlyClientData {
	client: string;
	amount: number;
}

interface MonthlyServiceData {
	service: string;
	amount: number;
}

interface MonthlyData {
	month: string;
	monthIndex: number;
	year: string;
	amount: string;
	regimes: MonthlyRegimeData[];
	clients: MonthlyClientData[];
	services: MonthlyServiceData[];
	fullDate?: Date; // Added for sorting by actual date
}

// Colors for different financial regimes
const REGIME_COLORS: { [key: string]: string } = {
	'Plataformas Tecnologicas': '#818CF8', // Indigo pastel
	'Sueldos y salarios': '#6EE7B7', // Emerald pastel
	'Actividades empresariales': '#FCD34D', // Amber pastel
	Arrendamiento: '#F9A8D4', // Pink pastel
	Resico: '#FFC107', // Naranja llamativo
	'Act. Empresarial': '#F9A8D4', // Pink pastel
	default: '#E5E7EB', // Gray pastel
};

// Month order for sorting
const monthOrder = [
	'Ene',
	'Feb',
	'Mar',
	'Abr',
	'May',
	'Jun',
	'Jul',
	'Ago',
	'Sep',
	'Oct',
	'Nov',
	'Dic',
];

const FinancialSummary = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [financialData, setFinancialData] = useState<MonthlyData[]>([]);
	const [groupedFinancialData, setGroupedFinancialData] = useState<{
		[key: string]: any;
	}>({});
	const [regimeTotals, setRegimeTotals] = useState<
		Array<{ name: string; amount: number }>
	>([]);
	const [selectedYear, setSelectedYear] = useState<string>('all'); // Default to "all" (last 12 months)
	const [availableYears, setAvailableYears] = useState<string[]>([]);
	const [selectedView, setSelectedView] = useState<'regimes' | 'period'>(
		'regimes',
	);
	const [selectedPeriodData, setSelectedPeriodData] =
		useState<MonthlyData | null>(null);
	const [selectedPeriodIndex, setSelectedPeriodIndex] = useState<number | null>(
		null,
	);

	const userProfile = useSelector((state: RootState) => state.profile.user);

	// Formatting functions
	const formatAmount = (amount: string | number) => {
		const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
		return `$${numAmount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	};

	const formatTotalAmount = (amount: string | number) => {
		const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
		return `$${numAmount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	};

	// Process data functions
	const processIncomeByRegime = (incomeString: string): MonthlyRegimeData[] => {
		const regimes: MonthlyRegimeData[] = [];
		const regimePairs = incomeString.split(
			incomeString?.includes('^') ? '^ ' : ', ',
		);

		regimePairs.forEach((pair) => {
			const [regime, amountStr] = pair.split(': ');
			if (regime && amountStr) {
				regimes.push({
					regime,
					amount: parseFloat(amountStr),
				});
			}
		});

		return regimes;
	};

	const processIncomeByClient = (incomeString: string): MonthlyClientData[] => {
		const clients: MonthlyClientData[] = [];
		const clientPairs = incomeString?.split(
			incomeString?.includes('^') ? '^ ' : ', ',
		);

		clientPairs.forEach((pair) => {
			const [client, amountStr] = pair.split(': ');
			if (client && amountStr) {
				clients.push({
					client,
					amount: parseFloat(amountStr),
				});
			}
		});

		return clients;
	};

	const processIncomeByService = (
		incomeString: string,
	): MonthlyServiceData[] => {
		const services: MonthlyServiceData[] = [];
		const servicePairs = incomeString?.split(
			incomeString?.includes('^') ? '^ ' : ', ',
		);

		servicePairs.forEach((pair) => {
			const [service, amountStr] = pair.split(': ');
			if (service && amountStr) {
				services.push({
					service,
					amount: parseFloat(amountStr),
				});
			}
		});

		return services;
	};

	const processMonthData = (monthData: MonthlyData) => {
		// Combine duplicate regimes
		const combinedRegimes = monthData.regimes.reduce(
			(acc: MonthlyRegimeData[], regime) => {
				const existingRegime = acc.find((r) => r.regime === regime.regime);
				if (existingRegime) {
					existingRegime.amount += regime.amount;
				} else {
					acc.push({ ...regime });
				}
				return acc;
			},
			[],
		);

		// Combine duplicate clients
		const combinedClients = monthData.clients.reduce(
			(acc: MonthlyClientData[], client) => {
				const existingClient = acc.find((c) => c.client === client.client);
				if (existingClient) {
					existingClient.amount += client.amount;
				} else {
					acc.push({ ...client });
				}
				return acc;
			},
			[],
		);

		// Combine duplicate services
		const combinedServices = monthData.services.reduce(
			(acc: MonthlyServiceData[], service) => {
				const existingService = acc.find((s) => s.service === service.service);
				if (existingService) {
					existingService.amount += service.amount;
				} else {
					acc.push({ ...service });
				}
				return acc;
			},
			[],
		);

		// Sort by amount (highest to lowest)
		combinedRegimes.sort((a, b) => b.amount - a.amount);
		combinedClients.sort((a, b) => b.amount - a.amount);
		combinedServices.sort((a, b) => b.amount - a.amount);

		return {
			...monthData,
			regimes: combinedRegimes,
			clients: combinedClients,
			services: combinedServices,
		};
	};

	// Get last 12 months of data
	const getLastTwelveMonths = (allData: MonthlyData[]) => {
		// Sort all data by date (newest first)
		const sortedData = [...allData].sort((a, b) => {
			const dateA = new Date(parseInt(a.year), a.monthIndex);
			const dateB = new Date(parseInt(b.year), b.monthIndex);
			return dateB.getTime() - dateA.getTime();
		});

		// Take the first 12 items or all if less than 12
		return sortedData.slice(0, 12).reverse(); // Reverse to show chronologically
	};

	// Calculate regime totals for a set of months
	const calculateRegimeTotals = (months: MonthlyData[]) => {
		const regimeTotals: Record<string, number> = {};

		months.forEach((month) => {
			month.regimes.forEach(({ regime, amount }) => {
				regimeTotals[regime] = (regimeTotals[regime] || 0) + amount;
			});
		});

		return Object.entries(regimeTotals).map(([name, amount]) => ({
			name,
			amount,
		}));
	};

	// Update display data when year changes
	const updateDisplayData = (year: string, data: any) => {
		// Collect all monthly data across all years
		const allMonthsArray: MonthlyData[] = [];
		Object.keys(data).forEach((yearKey) => {
			if (data[yearKey] && data[yearKey].monthlyData) {
				data[yearKey].monthlyData.forEach((month: MonthlyData) => {
					// Add full date for proper sorting
					const fullDate = new Date(parseInt(month.year), month.monthIndex);
					allMonthsArray.push({ ...month, fullDate });
				});
			}
		});

		// Show data based on selected year or "all" (last 12 months)
		if (year === 'all') {
			const last12Months = getLastTwelveMonths(allMonthsArray);
			setFinancialData(last12Months);

			// Calculate totals for the last 12 months
			const totalsByRegime = calculateRegimeTotals(last12Months);
			setRegimeTotals(totalsByRegime);
		} else if (data[year]) {
			// Show data for the specific year
			setFinancialData(data[year].monthlyData || []);

			// Update regime totals based on selected year
			const regimeTotalsData = Object.entries(data[year].regimeData || {}).map(
				([name, amount]) => ({
					name,
					amount,
				}),
			);
			setRegimeTotals(regimeTotalsData as { name: string; amount: number }[]);
		} else {
			setFinancialData([]);
			setRegimeTotals([]);
		}

		// Reset period selection
		setSelectedView('regimes');
		setSelectedPeriodData(null);
		setSelectedPeriodIndex(null);
	};

	// Fetch financial data from the API
	const fetchFinancialData = useCallback(async () => {
		if (!userProfile?.id) return;

		setIsLoading(true);
		try {
			const incomeDetails = await getUserIncomeFinancials();

			const sortedIncomes = incomeDetails.sort(
				(a: any, b: any) =>
					new Date(b.monthly_income).getTime() -
					new Date(a.monthly_income).getTime(),
			);

			const groupedByYear = sortedIncomes.reduce(
				(acc: { [key: string]: any }, detail: any) => {
					const date = new Date(detail.monthly_income);
					const utcDate = new Date(
						Date.UTC(
							date.getUTCFullYear(),
							date.getUTCMonth(),
							date.getUTCDate(),
						),
					);
					const year = utcDate.getUTCFullYear().toString();

					if (!acc[year]) {
						acc[year] = {
							monthlyData: [],
							regimeData: {},
						};
					}

					const regimes = processIncomeByRegime(
						detail.income_by_service_regime,
					);
					const clients = processIncomeByClient(detail.income_by_client);
					const services = processIncomeByService(detail.income_by_service);

					const monthIndex = utcDate.getUTCMonth();

					const existingMonthIndex = acc[year].monthlyData.findIndex(
						(m: MonthlyData) => m.monthIndex === monthIndex,
					);

					if (existingMonthIndex >= 0) {
						const existingMonth = acc[year].monthlyData[existingMonthIndex];
						acc[year].monthlyData[existingMonthIndex] = processMonthData({
							...existingMonth,
							amount: (
								Number(existingMonth.amount) + Number(detail.total_month_income)
							).toString(),
							regimes: [...existingMonth.regimes, ...regimes],
							clients: [...existingMonth.clients, ...clients],
							services: [...existingMonth.services, ...services],
						});
					} else {
						// Create new record for the month
						acc[year].monthlyData.push(
							processMonthData({
								month: monthOrder[monthIndex],
								monthIndex: monthIndex,
								amount: detail.total_month_income,
								year,
								regimes,
								clients,
								services,
							}),
						);
					}

					// Update regime totals
					regimes.forEach(
						({ regime, amount }: { regime: string; amount: number }) => {
							acc[year].regimeData[regime] =
								(acc[year].regimeData[regime] || 0) + amount;
						},
					);

					return acc;
				},
				{},
			);

			// Sort months within each year
			Object.keys(groupedByYear).forEach((year) => {
				groupedByYear[year].monthlyData.sort(
					(a: MonthlyData, b: MonthlyData) => a.monthIndex - b.monthIndex,
				);
			});

			const years = Object.keys(groupedByYear).sort((a, b) =>
				b.localeCompare(a),
			);

			setAvailableYears(years);
			setGroupedFinancialData(groupedByYear);

			// Always start with "all" (last 12 months) view
			updateDisplayData('all', groupedByYear);
		} catch (error) {
			// Set empty states in case of error
			setFinancialData([]);
			setRegimeTotals([]);
			setAvailableYears([]);
			setSelectedYear('');
			setGroupedFinancialData({});
		} finally {
			setIsLoading(false);
		}
	}, [userProfile?.id]);

	useEffect(() => {
		fetchFinancialData();
	}, []);

	// Handle period/bar click
	const handlePeriodClick = (data: MonthlyData, index: number) => {
		if (index === -1 || selectedPeriodIndex === index) {
			setSelectedView('regimes');
			setSelectedPeriodData(null);
			setSelectedPeriodIndex(null);
		} else {
			setSelectedView('period');
			setSelectedPeriodData(data);
			setSelectedPeriodIndex(index);
		}
	};

	// Handle year change
	const handleYearChange = (year: string) => {
		setSelectedYear(year);
		updateDisplayData(year, groupedFinancialData);
	};

	// Transform data for recharts
	const chartData = financialData.map((data) => {
		// Create a data object for recharts with month and total
		const monthData: any = {
			name: `${data.month} ${data.year.substring(2)}`,
			total: parseFloat(data.amount),
			rawData: data, // Keep the raw data for click handling
		};

		// Add each regime as a separate key in the data object
		data.regimes.forEach((regime) => {
			monthData[regime.regime] = regime.amount;
		});

		return monthData;
	});

	// Create bars for each unique regime across all months
	const uniqueRegimes = Array.from(
		new Set(
			financialData.flatMap((data) =>
				data.regimes.map((regime) => regime.regime),
			),
		),
	);

	// Custom tooltip component for the chart
	const CustomTooltip = ({
		active,
		payload,
		label,
	}: TooltipProps<number, string>) => {
		if (active && payload && payload.length) {
			return (
				<div className='bg-white p-3 shadow-md rounded-md border border-gray-200 text-sm'>
					<p className='font-semibold mb-1 text-gray-900'>{label}</p>
					<div className='space-y-1'>
						{payload.map((entry: any, index: number) => (
							<div
								key={index}
								className='flex items-center gap-2'
							>
								<div
									className='w-3 h-3 rounded-full'
									style={{ backgroundColor: entry.color }}
								/>
								<span className='text-gray-700'>
									{entry.name}: {formatAmount(entry.value as number)}
								</span>
							</div>
						))}
						<div className='pt-1 border-t border-gray-200 mt-1'>
							<span className='font-medium text-gray-800'>
								Total: {formatAmount(payload[0].payload.total)}
							</span>
						</div>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className='bg-white rounded-lg shadow-sm p-4 w-full'>
			<div className='flex items-center justify-between mb-4'>
				<div className='flex items-center gap-3'>
					<h3 className='text-lg font-bold text-gray-900'>
						Resumen de tus ingresos
					</h3>
				</div>
				<div className='flex items-center gap-2'>
					{selectedYear === 'all' && (
						<span className='text-xs text-gray-500'>
							Mostrando últimos 12 meses
						</span>
					)}
					{availableYears.length > 0 && (
						<select
							value={selectedYear}
							onChange={(e) => handleYearChange(e.target.value)}
							className='text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white'
						>
							<option value='all'>Todos</option>
							{availableYears.map((year) => (
								<option
									key={year}
									value={year}
								>
									{year}
								</option>
							))}
						</select>
					)}
				</div>
			</div>

			{isLoading ? (
				<Loading
					size={32}
					className='my-2 min-h-[250px]'
				/>
			) : financialData.length > 0 ? (
				<div className='flex flex-col gap-6'>
					{/* Bar chart with Recharts - Full width */}
					<div className='w-full'>
						<div className='h-[300px] w-full'>
							<ResponsiveContainer
								width='100%'
								height='100%'
							>
								<BarChart
									data={chartData}
									margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
									barGap={2}
									maxBarSize={40}
									onClick={(data) => {
										if (data && data.activePayload && data.activePayload[0]) {
											const clickedData = data.activePayload[0].payload.rawData;
											const index = financialData.findIndex(
												(d) =>
													d.month === clickedData.month &&
													d.year === clickedData.year,
											);

											handlePeriodClick(
												clickedData,
												index === selectedPeriodIndex ? -1 : index,
											);
										}
									}}
								>
									<XAxis
										dataKey='name'
										tick={{ fontSize: 12 }}
										tickMargin={10}
										axisLine={false}
										tickLine={false}
										interval={0}
										angle={-45}
										textAnchor='end'
										height={70}
									/>
									<YAxis
										hide
										domain={[0, 'dataMax']}
									/>
									<Tooltip
										content={<CustomTooltip />}
										cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
									/>
									{uniqueRegimes.map((regime) => (
										<Bar
											key={regime}
											dataKey={regime}
											stackId='a'
											fill={REGIME_COLORS[regime] || REGIME_COLORS.default}
											radius={[4, 4, 0, 0]}
											animationDuration={300}
										>
											{chartData.map((_entry, index) => (
												<Cell
													key={`cell-${index}`}
													fillOpacity={
														selectedPeriodIndex !== null &&
														selectedPeriodIndex !== index
															? 0.6
															: 1
													}
													stroke={
														selectedPeriodIndex === index ? '#3B82F6' : 'none'
													}
													strokeWidth={selectedPeriodIndex === index ? 2 : 0}
												/>
											))}
										</Bar>
									))}
								</BarChart>
							</ResponsiveContainer>
						</div>
					</div>

					{/* Regime list / period details */}
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
						<h4 className='text-sm font-medium text-gray-700 col-span-full mb-1'>
							{selectedView === 'regimes'
								? 'Total por Régimen'
								: `Desglose ${selectedPeriodData?.month} ${selectedPeriodData?.year}`}
						</h4>

						{selectedView === 'regimes' ? (
							regimeTotals.map((regime) => (
								<div
									key={regime.name}
									className='flex items-center justify-between p-3 rounded-lg'
								>
									<div className='flex items-center gap-2'>
										<div
											className='w-3 h-3 rounded-full'
											style={{
												backgroundColor:
													REGIME_COLORS[regime.name] || REGIME_COLORS.default,
											}}
										/>
										<span className='text-sm text-gray-600'>{regime.name}</span>
									</div>
									<span className='text-sm font-medium'>
										{formatTotalAmount(regime.amount)}
									</span>
								</div>
							))
						) : (
							<div className='col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
								{selectedPeriodData?.clients.map((client, index) => {
									const clientServices = selectedPeriodData.services.filter(
										(service) => service.amount === client.amount,
									);

									return (
										<div
											key={index}
											className='p-2 rounded-lg'
										>
											<div className='flex items-center justify-between'>
												<span className='text-sm font-semibold text-gray-700'>
													{client.client}
												</span>
												<span className='text-sm font-medium'>
													{formatAmount(client.amount)}
												</span>
											</div>
											{clientServices.map((service, sIndex) => (
												<div
													key={sIndex}
													className='mt-1 pl-3 text-xs text-gray-500 flex justify-between items-center border-l-2 border-gray-200'
												>
													<span className='text-gray-600'>
														{service.service}
													</span>
												</div>
											))}
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			) : (
				<div className='flex flex-col items-center justify-center py-12'>
					<Warning
						size={48}
						weight='duotone'
						className='text-yellow-500 mb-4'
					/>
					<p className='text-gray-600 text-center mb-2'>
						No hay información de ingresos disponible
					</p>
					<p className='text-sm text-gray-500 text-center'>
						Los datos se mostrarán una vez que se procesen las extracciones
					</p>
				</div>
			)}
		</div>
	);
};

export default FinancialSummary;
